'use client'

import { useState, useRef, useEffect } from 'react'
import { AIRPORT_DB, FLAG_COUNTRY } from '@/data/airports'

export { AIRPORT_DB, FLAG_COUNTRY }

/**
 * AirportCombobox — searchable airport picker with flag + city autocomplete.
 *
 * Props:
 *   airports   – string[]  restrict to these IATAs (optional; defaults to full AIRPORT_DB)
 *   exclude    – string[]  IATAs to hide from suggestions (for multi-select "add" mode)
 *   value      – string    selected IATA code ('' = nothing selected / add-mode)
 *   onChange   – fn(iata)  called on selection
 *   placeholder– string
 *   getLabel   – fn(iata) → string  extra label shown on the right of each row (e.g. deal count)
 *   clearable  – bool      show ✕ button when a value is selected (default true)
 */
export default function AirportCombobox({
  airports,
  exclude = [],
  value = '',
  onChange,
  placeholder = 'Search airport…',
  getLabel,
  clearable = true,
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(0)
  const wrapRef = useRef(null)
  const inputRef = useRef(null)

  // Build candidate list (restricted + exclude applied)
  const candidates = (airports ?? Object.keys(AIRPORT_DB))
    .filter((iata) => !exclude.includes(iata))
    .map((iata) => ({
      iata,
      city: AIRPORT_DB[iata]?.city ?? iata,
      flag: AIRPORT_DB[iata]?.flag ?? '✈️',
    }))

  // Filter by query
  const q = query.trim().toLowerCase()
  const suggestions = q
    ? candidates.filter(
        (ap) =>
          ap.iata.toLowerCase().startsWith(q) ||
          ap.city.toLowerCase().includes(q)
      )
    : candidates

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Scroll highlighted item into view
  useEffect(() => {
    if (!open) return
    const list = wrapRef.current?.querySelector('[role="listbox"]')
    const item = list?.children[cursor]
    item?.scrollIntoView({ block: 'nearest' })
  }, [cursor, open])

  function select(iata) {
    onChange(iata)
    setQuery('')
    setOpen(false)
    setCursor(0)
  }

  function handleKey(e) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { setOpen(true); setCursor(0) }
      return
    }
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, suggestions.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)) }
    if (e.key === 'Enter' && suggestions[cursor]) { e.preventDefault(); select(suggestions[cursor].iata) }
  }

  const info = value ? AIRPORT_DB[value] : null
  const displayValue = value
    ? info ? `${info.flag} ${value} — ${info.city}` : value
    : ''

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 transition-shadow focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500">
        <span className="shrink-0 text-sm text-gray-400" aria-hidden="true">✈</span>
        <input
          ref={inputRef}
          type="text"
          value={open ? query : displayValue}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setCursor(0) }}
          onFocus={() => { setQuery(''); setOpen(true); setCursor(0) }}
          onKeyDown={handleKey}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
        />
        {clearable && value && !open && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onChange(''); setQuery('') }}
            aria-label="Clear selection"
            className="shrink-0 leading-none text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
        {open && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setOpen(false) }}
            aria-label="Close"
            className="shrink-0 leading-none text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {suggestions.map((ap, i) => (
            <li
              key={ap.iata}
              role="option"
              aria-selected={ap.iata === value}
              onMouseDown={(e) => { e.preventDefault(); select(ap.iata) }}
              onMouseEnter={() => setCursor(i)}
              className={`flex cursor-pointer select-none items-center gap-2 px-3 py-2.5 text-sm ${
                i === cursor
                  ? 'bg-blue-50 text-blue-700'
                  : ap.iata === value
                    ? 'bg-gray-50 font-medium text-gray-900'
                    : 'text-gray-800 hover:bg-gray-50'
              }`}
            >
              <span className="w-6 shrink-0 text-center text-base leading-none">{ap.flag}</span>
              <span className="w-9 shrink-0 font-mono font-semibold">{ap.iata}</span>
              <span className="text-gray-300">·</span>
              <span className="flex-1 truncate">{ap.city}</span>
              {getLabel && (
                <span className="shrink-0 text-xs text-gray-400">{getLabel(ap.iata)}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && suggestions.length === 0 && q && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 shadow-lg">
          No airport found for &ldquo;{query}&rdquo;
        </div>
      )}
    </div>
  )
}
